import { useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { guidEquals } from "../../utils/guid";
import styles from "./ImportProjectsModal.module.css";
import ExcelPasteGrid, {
  cellsToTsv,
  tsvToCells,
  ExcelPasteGridValue,
} from "../ExcelPasteGrid/ExcelPasteGrid";
import {
  createProject,
  fetchAllCommunityEntitiesAdmin,
  fetchAllCollections,
  addProjectToCollection,
} from "../../api";
import type {
  CommunityEntityResponse,
  CollectionResponse,
  CreateProjectRequest,
} from "../../api";

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful import so the caller can refetch projects. */
  onImported?: () => void;
  /**
   * 'modal' (default) — renders as a floating overlay with backdrop.
   * 'embedded' — renders just the form body, no overlay/header chrome.
   */
  mode?: "modal" | "embedded";
  /** Pre-select a community. When provided, the community dropdown is locked. */
  communityGuid?: string;
  communityName?: string;
};

/* ─── Default-field option lists ─────────────────────────────── */

const PROJECT_CATEGORIES = [
  "", "infrastructure", "education", "health", "agriculture",
  "water_sanitation", "energy", "technology", "environment",
  "governance", "economic_development", "social_services", "other",
];

const CONTENT_STATUSES = [
  "", "draft", "public", "restricted", "archived",
];

const PROJECT_STATUSES = [
  "", "active", "in_progress", "planning", "on_hold", "completed", "cancelled",
];

const PROJECT_REACH = [
  "", "local", "regional", "national", "international",
];

/* ─── Helpers ────────────────────────────────────────────────── */

function normHeader(h: string): string {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

/** Normalize any date input to MM/dd/yyyy format. */
function normalizeDate(input: any): string {
  if (input === null || input === undefined) return "";

  let raw = String(input)
    .replace(/\u00A0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();
  if (!raw) return "";

  raw = raw.replace(/[\u2010-\u2015\u2212]/g, "-");

  // Helper: convert a Date to MM/dd/yyyy
  const toMdy = (d: Date): string => {
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // Already ISO yyyy-MM-dd → convert to MM/dd/yyyy
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [yyyy, mm, dd] = raw.split("-");
    return `${mm}/${dd}/${yyyy}`;
  }

  // Already MM/dd/yyyy or M/d/yyyy → normalize padding
  const mdySlash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (mdySlash) {
    const mm = String(mdySlash[1]).padStart(2, "0");
    const dd = String(mdySlash[2]).padStart(2, "0");
    let yyyy = mdySlash[3];
    if (yyyy.length === 2) yyyy = `20${yyyy}`;
    return `${mm}/${dd}/${yyyy}`;
  }

  // MM-dd-yyyy or M-d-yyyy with dashes
  const mdyDash = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})$/);
  if (mdyDash) {
    const mm = String(mdyDash[1]).padStart(2, "0");
    const dd = String(mdyDash[2]).padStart(2, "0");
    let yyyy = mdyDash[3];
    if (yyyy.length === 2) yyyy = `20${yyyy}`;
    return `${mm}/${dd}/${yyyy}`;
  }

  // Excel serial number
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const d = new Date(excelEpoch.getTime() + n * 86400000);
      return toMdy(d);
    }
  }

  // Generic Date.parse fallback
  const t = Date.parse(raw);
  if (!Number.isNaN(t)) return toMdy(new Date(t));

  return "";
}

function normalizeCellText(v: any): string {
  return String(v ?? "")
    .replace(/\u00A0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();
}

function splitRow(line: string, delimiter: "tab" | "spaces"): string[] {
  if (delimiter === "tab") return String(line ?? "").split("\t");
  return normalizeCellText(line).split(/\s{2,}/g);
}

function detectDelimiter(headerLine: string): "tab" | "spaces" {
  if (headerLine.includes("\t")) return "tab";
  if (/\s{2,}/.test(headerLine)) return "spaces";
  return "tab";
}

function parseTsv(text: string): {
  headers: string[];
  rows: Record<string, string>[];
} {
  const lines = (text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    // Only trim trailing spaces/NBSP — NOT tabs (tab is a delimiter)
    .map((l) => String(l ?? "").replace(/[ \u00A0]+$/g, ""))
    .filter((l) => normalizeCellText(l).length > 0);

  if (!lines.length) return { headers: [], rows: [] };

  const delimiter = detectDelimiter(lines[0]);
  const rawHeaders = splitRow(lines[0], delimiter);
  const headers = rawHeaders.map(normHeader);
  const expectedCols = Math.max(1, headers.length);
  const startIdx = headers.indexOf("project_start_date");

  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    let parts = splitRow(lines[i], delimiter);

    // ── Line-stitching for Excel line breaks inside project_name ──
    // If this row doesn't have a parseable start date but the next row does,
    // merge them: concatenate project_name fragments, take remaining columns
    // from the row that actually has the date.
    if (startIdx >= 0) {
      const startHere = normalizeDate(parts[startIdx] ?? "");
      if (!startHere && i + 1 < lines.length) {
        const nextParts = splitRow(lines[i + 1], delimiter);
        const nextStart = normalizeDate(nextParts[startIdx] ?? "");
        if (nextStart) {
          const merged: string[] = [];
          // Merge project_name fragments
          merged[0] = String(parts[0] ?? "").trim() + " " + String(nextParts[0] ?? "").trim();
          // Take the rest of the columns from the row that has the date
          for (let c = 1; c < expectedCols; c++) {
            merged[c] = String(nextParts[c] ?? "").trim();
          }
          parts = merged;
          i += 1; // skip next line (consumed)
        }
      }
    }

    // If lat/lon were pasted into one cell ("41.49 -71.31"), split it.
    if (
      parts.length === headers.length - 1 &&
      headers.includes("latitude") &&
      headers.includes("longitude")
    ) {
      const last = normalizeCellText(parts[parts.length - 1] ?? "");
      const m = last.match(/^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)$/);
      if (m) parts = [...parts.slice(0, -1), m[1], m[2]];
    }

    // Pad to expected columns so header mapping is stable
    if (parts.length < expectedCols) {
      parts = [...parts, ...new Array(expectedCols - parts.length).fill("")];
    }

    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = normalizeCellText(parts[idx] ?? "");
    });

    // Skip blank rows (common when pasting from Excel)
    const allEmpty = Object.values(row).every((v) => v.length === 0);
    if (!allEmpty) rows.push(row);
  }

  return { headers, rows };
}

function toNumberOrEmpty(v: any): string {
  const raw = String(v ?? "").trim();
  if (!raw) return "";
  const num = Number(raw);
  return Number.isFinite(num) ? String(num) : raw;
}

function labelFromSlug(s: string): string {
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ─── Component ──────────────────────────────────────────────── */

export default function ImportProjectsModal({
  isOpen,
  onClose,
  onImported,
  mode = "modal",
  communityGuid: propCommunityGuid,
  communityName: propCommunityName,
}: Props) {
  const { t } = useAppTranslation('pages');
  const defaultHeader =
    "Project Name\tProject Start Date\tProject End Date\tLatitude\tLongitude\tLocation GeoJSON\n";

  const isEmbedded = mode === "embedded";
  const communityLocked = !!propCommunityGuid;

  // ── Dropdowns state ─────────────────────────────────────────
  const [selectedCommunityGuid, setSelectedCommunityGuid] = useState<string>(propCommunityGuid || "");
  const [selectedCollectionGuid, setSelectedCollectionGuid] = useState<string>("");
  const [defaultCategory, setDefaultCategory] = useState<string>("");
  const [defaultContentStatus, setDefaultContentStatus] = useState<string>("public");
  const [defaultProjectStatus, setDefaultProjectStatus] = useState<string>("active");
  const [defaultProjectReach, setDefaultProjectReach] = useState<string>("");

  // Sync if prop changes
  useEffect(() => {
    if (propCommunityGuid) setSelectedCommunityGuid(propCommunityGuid);
  }, [propCommunityGuid]);

  // ── Grid state ──────────────────────────────────────────────
  const [gridValue, setGridValue] = useState<ExcelPasteGridValue>(() => ({
    cells: tsvToCells(defaultHeader),
  }));

  const paste = useMemo(() => cellsToTsv(gridValue.cells), [gridValue]);

  // Reset status indicators when grid content changes
  useEffect(() => {
    setValidateStatus(null);
    setImportStatus(null);
    setShowValidation(false);
  }, [paste]);

  const [lastImportSummary, setLastImportSummary] = useState<string>("");
  const [showValidation, setShowValidation] = useState(false);
  const [importing, setImporting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Status indicators: null = not yet run, "valid" | "errors" for validate, "success" | "partial" | "fail" for import
  const [validateStatus, setValidateStatus] = useState<"valid" | "errors" | null>(null);
  const [importStatus, setImportStatus] = useState<"success" | "partial" | "fail" | null>(null);

  // ── API data for dropdowns ──────────────────────────────────
  const [communityEntities, setCommunityEntities] = useState<CommunityEntityResponse[]>([]);
  const [collections, setCollections] = useState<CollectionResponse[]>([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setLastImportSummary("");
      setShowValidation(false);
      setApiError(null);
      setValidateStatus(null);
      setImportStatus(null);
      return;
    }

    let cancelled = false;
    setDropdownsLoading(true);

    Promise.all([
      fetchAllCommunityEntitiesAdmin().catch(() => [] as CommunityEntityResponse[]),
      fetchAllCollections().catch(() => [] as CollectionResponse[]),
    ]).then(([entities, colls]) => {
      if (cancelled) return;
      setCommunityEntities(entities);
      setCollections(colls);

      if (!entities.length && !colls.length) {
        setApiError("API unavailable — cannot load communities/collections. Please ensure the API is running.");
      }
    }).finally(() => {
      if (!cancelled) setDropdownsLoading(false);
    });

    return () => { cancelled = true; };
  }, [isOpen]);

  const parsed = useMemo(() => parseTsv(paste), [paste]);

  const validation = useMemo(() => {
    const headerErrors: string[] = [];
    const required = ["project_name", "project_start_date"];
    for (const r of required) {
      if (!parsed.headers.includes(r))
        headerErrors.push(`Missing required column: ${r}`);
    }

    if (!selectedCommunityGuid && !parsed.headers.includes("community_guid")) {
      headerErrors.push("Select a Community (or include a community_guid column).");
    }
    if (!defaultProjectStatus && !parsed.headers.includes("project_status")) {
      headerErrors.push("Set a default Project Status (or include a project_status column).");
    }
    if (!defaultContentStatus && !parsed.headers.includes("content_status")) {
      headerErrors.push("Set a default Content Status (or include a content_status column).");
    }

    const rowErrors: string[] = [];

    parsed.rows.forEach((row, idx) => {
      const rowNum = idx + 2;
      const name = (row.project_name || "").trim();
      const comm = String(row.community_guid || "").trim() || selectedCommunityGuid;
      const startRaw = normalizeCellText(row.project_start_date ?? "");
      const endRaw = normalizeCellText(row.project_end_date ?? "");
      const start = normalizeDate(startRaw);
      const end = normalizeDate(endRaw);

      // Row value or default dropdown — must resolve to something
      const effectiveProjectStatus = String(row.project_status || "").trim() || defaultProjectStatus;
      const effectiveContentStatus = String(row.content_status || "").trim() || defaultContentStatus;

      if (!name) rowErrors.push(`Row ${rowNum}: Project Name is required`);
      if (!comm)
        rowErrors.push(`Row ${rowNum}: Community is required (select one or include a community_guid column)`);
      if (!startRaw) rowErrors.push(`Row ${rowNum}: Project Start Date is required`);
      else if (!start) rowErrors.push(`Row ${rowNum}: invalid Project Start Date: ${row.project_start_date}`);
      if (!effectiveProjectStatus)
        rowErrors.push(`Row ${rowNum}: Project Status is required (set a default or include a project_status column)`);
      if (!effectiveContentStatus)
        rowErrors.push(`Row ${rowNum}: Content Status is required (set a default or include a content_status column)`);

      if (start && Number.isNaN(Date.parse(start)))
        rowErrors.push(
          `Row ${rowNum}: invalid project_start_date: ${row.project_start_date}`,
        );
      if (end && Number.isNaN(Date.parse(end)))
        rowErrors.push(
          `Row ${rowNum}: invalid project_end_date: ${row.project_end_date}`,
        );
      if (
        start &&
        end &&
        !Number.isNaN(Date.parse(start)) &&
        !Number.isNaN(Date.parse(end))
      ) {
        if (new Date(end).getTime() < new Date(start).getTime())
          rowErrors.push(`Row ${rowNum}: project_end_date is before start`);
      }

      const latRaw = (row.latitude || row.location_lat || "").trim();
      const lonRaw = (row.longitude || row.location_lon || "").trim();
      if ((latRaw && !lonRaw) || (!latRaw && lonRaw))
        rowErrors.push(
          `Row ${rowNum}: latitude and longitude must both be provided`,
        );
      if (latRaw) {
        const lat = Number(latRaw);
        if (!Number.isFinite(lat) || lat < -90 || lat > 90)
          rowErrors.push(`Row ${rowNum}: invalid latitude: ${latRaw}`);
      }
      if (lonRaw) {
        const lon = Number(lonRaw);
        if (!Number.isFinite(lon) || lon < -180 || lon > 180)
          rowErrors.push(`Row ${rowNum}: invalid longitude: ${lonRaw}`);
      }
    });

    return {
      headerErrors,
      rowErrors,
      ok: headerErrors.length === 0 && rowErrors.length === 0,
      rowCount: parsed.rows.length,
    };
  }, [parsed.headers, parsed.rows, selectedCommunityGuid, defaultProjectStatus, defaultContentStatus]);

  const preview = useMemo(() => {
    return parsed.rows.slice(0, 5).map((r) => ({
      project_name: (r.project_name || "").trim(),
      community_guid: (r.community_guid || "").trim() || selectedCommunityGuid,
      collection_guid: selectedCollectionGuid || "(none)",
      project_start_date: normalizeDate(r.project_start_date),
      project_end_date: normalizeDate(r.project_end_date),
      project_category: (r.project_category || "").trim() || defaultCategory || "(default)",
      content_status: (r.content_status || "").trim() || defaultContentStatus || "(default)",
      project_status: (r.project_status || "").trim() || defaultProjectStatus || "(default)",
    }));
  }, [parsed.rows, selectedCommunityGuid, selectedCollectionGuid, defaultCategory, defaultContentStatus, defaultProjectStatus]);

  if (!isOpen) return null;

  const doImport = async () => {
    if (!validation.ok || validation.rowCount === 0) {
      setShowValidation(true);
      setValidateStatus("errors");
      return;
    }

    setImporting(true);
    setApiError(null);
    setLastImportSummary("");
    setImportStatus(null);

    let created = 0;
    let failed = 0;
    let collectionLinked = 0;
    const errors: string[] = [];

    for (const row of parsed.rows) {
      const start = normalizeDate(row.project_start_date);
      const end = normalizeDate(row.project_end_date);
      const lat = (row.latitude || row.location_lat || "").trim();
      const lon = (row.longitude || row.location_lon || "").trim();
      const communityGuid = String(row.community_guid || "").trim() || selectedCommunityGuid;

      // Row values override defaults; defaults apply when row column is empty
      const category = String(row.project_category || "").trim() || defaultCategory;
      const contentSt = String(row.content_status || "").trim() || defaultContentStatus;
      const projectSt = String(row.project_status || "").trim() || defaultProjectStatus;
      const reach = String(row.project_reach || "").trim() || defaultProjectReach;

      const request: CreateProjectRequest = {
        name: String(row.project_name || "").trim(),
        startDate: start || undefined,
        finishDate: end || undefined,
        projectCategory: category || undefined,
        projectStatus: projectSt || undefined,
        contentStatus: contentSt || "public",
        projectReach: reach || undefined,
        communityId: communityGuid || undefined,
        locationLat: toNumberOrEmpty(lat) || undefined,
        locationLon: toNumberOrEmpty(lon) || undefined,
        budgetEstimate: String(row.budget_estimate || "").trim() || undefined,
        budgetCurrency: String(row.budget_currency || "").trim() || undefined,
      };

      try {
        const newProject = await createProject(request);
        created += 1;

        // If a collection is selected, link this project to it
        if (selectedCollectionGuid && newProject?.id) {
          try {
            await addProjectToCollection(selectedCollectionGuid, newProject.id);
            collectionLinked += 1;
          } catch (linkErr: any) {
            console.warn(`[Import] Could not add project to collection:`, linkErr.message);
          }
        }
      } catch (err: any) {
        failed += 1;
        const name = request.name || `Row ${created + failed + 1}`;
        errors.push(`${name}: ${err.message}`);
      }
    }

    setImporting(false);

    if (failed > 0) {
      setApiError(`${failed} project(s) failed to import:\n${errors.slice(0, 5).join("\n")}${errors.length > 5 ? `\n...and ${errors.length - 5} more` : ""}`);
    }

    const parts: string[] = [];
    if (created > 0) parts.push(`Imported ${created} project(s)`);
    if (collectionLinked > 0) {
      const collName = collections.find(c => guidEquals(c.collectionGuid, selectedCollectionGuid))?.collectionName || "collection";
      parts.push(`added to "${collName}"`);
    }
    if (failed > 0) parts.push(`${failed} failed`);

    if (parts.length) {
      setLastImportSummary(parts.join(", ") + ".");
    }

    // Set import status indicator
    if (failed === 0 && created > 0) {
      setImportStatus("success");
    } else if (created > 0 && failed > 0) {
      setImportStatus("partial");
    } else {
      setImportStatus("fail");
    }

    if (created > 0) {
      onImported?.();
    }
  };

  const clearGrid = () => {
    setGridValue({ cells: tsvToCells(defaultHeader) });
    setShowValidation(false);
    setApiError(null);
    setLastImportSummary("");
    setValidateStatus(null);
    setImportStatus(null);
  };

  const handleValidate = () => {
    setShowValidation(true);
    setValidateStatus(validation.ok ? "valid" : "errors");
  };

  const activeCommunities = communityEntities
    .filter((e) => String(e.entityType || "").indexOf("geo_") !== 0)
    .filter((e) => e.isActive !== false);

  const activeCollections = collections.filter((c) => c.isActive !== false);

  /* ─── The form body — shared between modal and embedded modes ── */
  const formBody = (
    <>
      {/* Community dropdown */}
      {communityLocked ? (
        <div className={styles.help} style={{ marginTop: 0 }}>
          Community: <b>{propCommunityName || propCommunityGuid}</b>
        </div>
      ) : (
        <>
          <div className={styles.help} style={{ marginTop: 0 }}>
            Community (applies to all rows unless a <b>community_guid</b> column is provided):
          </div>
          <select
            className={styles.select}
            value={selectedCommunityGuid}
            onChange={(e) => {
              setSelectedCommunityGuid(e.target.value);
              setShowValidation(false);
            }}
            disabled={importing || dropdownsLoading}
          >
            <option value="">
              {dropdownsLoading ? "Loading..." : "-- Select Community --"}
            </option>
            {activeCommunities.map((e) => (
              <option key={String(e.entityGuid)} value={String(e.entityGuid)}>
                {String(e.name)} ({String(e.entityType)})
              </option>
            ))}
          </select>
        </>
      )}

      {/* Collection dropdown */}
      <div className={styles.help} style={{ marginTop: 4 }}>
        Collection (imported projects will be added to this collection):
      </div>
      <select
        className={styles.select}
        value={selectedCollectionGuid}
        onChange={(e) => setSelectedCollectionGuid(e.target.value)}
        disabled={importing || dropdownsLoading}
      >
        <option value="">
          {dropdownsLoading ? "Loading..." : "-- No Collection (skip) --"}
        </option>
        {activeCollections.map((c) => (
          <option key={String(c.collectionGuid)} value={String(c.collectionGuid)}>
            {String(c.collectionName)}{c.description ? ` — ${c.description}` : ""}
          </option>
        ))}
      </select>

      {/* Default field dropdowns */}
      <div className={styles.help} style={{ marginTop: 4 }}>
        Default field values (used when row data does not include these columns):
      </div>
      <div className={styles.defaultsGrid}>
        <div className={styles.fieldGroup}>
          <label>{t('import.projectCategory')}</label>
          <select value={defaultCategory} onChange={(e) => setDefaultCategory(e.target.value)} disabled={importing}>
            {PROJECT_CATEGORIES.map((v) => (
              <option key={v} value={v}>{v ? t(`import.enums.projectCategory.${v}`, { defaultValue: labelFromSlug(v) }) : "-- None --"}</option>
            ))}
          </select>
        </div>
        <div className={styles.fieldGroup}>
          <label>{t('import.contentStatus')}</label>
          <select value={defaultContentStatus} onChange={(e) => setDefaultContentStatus(e.target.value)} disabled={importing}>
            {CONTENT_STATUSES.map((v) => (
              <option key={v} value={v}>{v ? t(`import.enums.contentStatus.${v}`, { defaultValue: labelFromSlug(v) }) : "-- None --"}</option>
            ))}
          </select>
        </div>
        <div className={styles.fieldGroup}>
          <label>{t('import.projectStatus')}</label>
          <select value={defaultProjectStatus} onChange={(e) => setDefaultProjectStatus(e.target.value)} disabled={importing}>
            {PROJECT_STATUSES.map((v) => (
              <option key={v} value={v}>{v ? t(`import.enums.projectStatus.${v}`, { defaultValue: labelFromSlug(v) }) : "-- None --"}</option>
            ))}
          </select>
        </div>
        <div className={styles.fieldGroup}>
          <label>{t('import.projectReach')}</label>
          <select value={defaultProjectReach} onChange={(e) => setDefaultProjectReach(e.target.value)} disabled={importing}>
            {PROJECT_REACH.map((v) => (
              <option key={v} value={v}>{v ? t(`import.enums.projectReach.${v}`, { defaultValue: labelFromSlug(v) }) : "-- None --"}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Paste grid with hint */}
      <div className={styles.gridHintRow}>
        <span className={styles.tipIcon} title={t('import.pasteTitle')}>?</span>
        <span className={styles.gridHint}>{t('import.pasteHint')}</span>
      </div>
      <ExcelPasteGrid
        value={gridValue}
        onChange={setGridValue}
        minRows={10}
        minCols={6}
        lockedRows={1}
        showAddRowCol={false}
      />

      {/* ── Action buttons — right-aligned below grid ── */}
      <div className={styles.actionRow}>
        <button
          className={`${styles.btn} ${validateStatus === "valid" ? styles.btnSuccess : validateStatus === "errors" ? styles.btnWarning : ""}`}
          onClick={handleValidate}
          disabled={importing}
        >
          {validateStatus === "valid" ? "\u2705 Valid" : validateStatus === "errors" ? "\u26A0\uFE0F Errors" : "Validate"}
        </button>
        <button
          className={`${styles.btn} ${styles.btnPrimary} ${importStatus === "success" ? styles.btnSuccess : importStatus === "fail" ? styles.btnFail : ""}`}
          onClick={doImport}
          disabled={importing}
        >
          {importing ? "Importing..." : importStatus === "success" ? "\u2705 Imported" : importStatus === "fail" ? "\u274C Failed" : importStatus === "partial" ? "\u26A0\uFE0F Partial" : "Import"}
        </button>
      </div>

      {/* ── Status banners ── */}
      {showValidation &&
      (validation.headerErrors.length > 0 ||
        validation.rowErrors.length > 0) ? (
        <div className={styles.errors}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {"\u26A0\uFE0F"} Validation issues
          </div>
          {validation.headerErrors.map((e, i) => (
            <div key={`h-${i}`} className={styles.small}>
              {"\u2022"} {e}
            </div>
          ))}
          {validation.rowErrors.slice(0, 15).map((e, i) => (
            <div key={`r-${i}`} className={styles.small}>
              {"\u2022"} {e}
            </div>
          ))}
          {validation.rowErrors.length > 15 ? (
            <div className={styles.small}>
              …and {validation.rowErrors.length - 15} more
            </div>
          ) : null}
        </div>
      ) : showValidation && validation.ok ? (
        <div className={styles.successBanner}>
          {"\u2705"} Parsed <b>{validation.rowCount}</b> row{validation.rowCount !== 1 ? "s" : ""}. Data is valid and ready to import.
        </div>
      ) : null}

      {apiError ? (
        <div className={styles.errors}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {"\u274C"} Import errors
          </div>
          <pre className={styles.small} style={{ whiteSpace: "pre-wrap", margin: 0 }}>
            {apiError}
          </pre>
        </div>
      ) : null}

      {importStatus === "success" && lastImportSummary ? (
        <div className={styles.successBanner}>
          {"\u2705"} {lastImportSummary}
        </div>
      ) : importStatus === "partial" && lastImportSummary ? (
        <div className={styles.warningBanner}>
          {"\u26A0\uFE0F"} {lastImportSummary}
        </div>
      ) : null}

      {/* ── Collapsible data preview ── */}
      <details className={styles.previewDetails}>
        <summary className={styles.previewSummary}>
          Data Preview ({validation.rowCount} row{validation.rowCount !== 1 ? "s" : ""})
        </summary>
        <div className={styles.previewScroll}>
          {preview.length ? (
            <pre
              className={styles.small}
              style={{ whiteSpace: "pre-wrap", margin: 0 }}
            >
              {JSON.stringify(preview, null, 2)}
            </pre>
          ) : (
            <div className={styles.small}>Paste rows to preview.</div>
          )}
        </div>
      </details>
    </>
  );

  /* ─── Embedded mode: just return the form body ─────────────── */
  if (isEmbedded) {
    return <div className={styles.body}>{formBody}</div>;
  }

  /* ─── Modal mode: wrap in overlay + chrome ─────────────────── */
  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{t('import.title')}</h2>
            <p className={styles.subtitle}>
              {t('import.pastePreview')}
            </p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} disabled={importing}>
            Close
          </button>
        </div>

        <div className={styles.body}>{formBody}</div>
      </div>
    </div>
  );
}
